import { useStore } from 'hooks/useStore';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { unconstrainedForecastService } from 'services/fdp/unconstrainedForecastService';
import { deepCopy } from 'utils/deepCopy';
import { forecastDataInterface } from 'Interfaces/FDPInterfaces/unconstrainedForecast';
import { convertIsoToDateString } from 'utils/dateUtilities';

type valueTypeInterface = 'saved' | 'old' | 'forecast';

export const useUnconstraiedForecastEdit = () => {
  const {
    UserStore: { userModulesRoles },
    NavbarStore: { isNavigationRestricted, setisNavigationRestricted },
    UnconstrainedForecastViewStore: { requestProgressRefresh },
    UnconstrainedForecastEditStore: {
      conceptDetails,
      contractDetails,
      isConceptLoading,
      directNDCheading,
      indirectNDCheading,
      directcurrentNDCIndex,
      indirectcurrentNDCIndex,
      unconstrainedForecastEdit,
      isWholeSalerDateLoading,
      isInvoiceSalesLoading,
      invoiceSalesSummary,
      isUnconstrainedForecastNotesDataLoading,
      isUnconstrainedNoteSaving,
      UnconstrainedForecastNotesData,
      oldunconstrainedForecastEdit,
      isDirectMode,
      isUnconstrainedNoteSaved,
      setIsDirectMode,
      setDirectCurrentTabIndex,
      setIndirectCurrentTabIndex,
      handleGetConceptDetails,
      fetchForecastGFCNotes,
      saveForecastNote,
      updateUnconstrainedForecastEdit,
      updateOldeditState,
      setNoteData,
      removeNoteData,
      resetStore,
    },
  } = useStore();
  const navigate = useNavigate();

  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [modalVisibe, setModalVisible] = useState<boolean>(false);
  const [finishmodal, setFinishModal] = useState<boolean>(false);
  const [finishModalForCategory, setFinishModalForCategory] = useState<boolean>(false);
  const [ndcValueTypeUsing, setNdcValueTypeUsing] = useState<
    Map<string, 'saved' | 'old' | 'forecast'>
  >(new Map());
  const [ndcUsingPercentage, setNdcUsingPercentage] = useState<Set<string>>(new Set());
  const [savingAll, setSavingAll] = useState<boolean>(false);
  const [savingSingle, setSavingSingle] = useState<boolean>(false);
  const [savingForCategory, setSavingForCategory] = useState<boolean>(false);
  const [copyRowInfo, setCopyrowInfo] = useState<Set<string>>(new Set());
  const [isNoteEdited, setIsNoteEdited] = useState<Map<number, boolean>>(new Map());
  const [isNoteDeleted, setIsNoteDeleted] = useState<Map<number, boolean>>(new Map());

  const directInputFocus = useRef<HTMLInputElement[]>([]);
  const indirectInputFocus = useRef<HTMLInputElement[]>([]);
  const [directFocused, setDirectFocused] = useState<number | null>(null);
  const [indirectFocused, setIndirectFocused] = useState<number | null>(null);

  const { gfc } = useParams();

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    fetchForecastGFCNotes(
      isDirectMode
        ? directNDCheading[directcurrentNDCIndex]?.ndc
        : indirectNDCheading[indirectcurrentNDCIndex]?.ndc,
      signal
    );

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional deps
  }, [directcurrentNDCIndex, indirectcurrentNDCIndex, isDirectMode]);

  useEffect(() => {
    // getting the unconstrained forecast edit details
    handleGetConceptDetails(Number(gfc));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on mount only
  }, []);

  useEffect(() => {
    window.scroll(0, 0);
    setisNavigationRestricted(true);

    return () => {
      setisNavigationRestricted(false);
      resetStore();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on mount only
  }, []);


  const getFutureMonthsEdit = () => {
    const arr: { label: string; dateString: string }[] = [];
    const dt = new Date();
    const startMonth = dt.getMonth();
    const startYear = dt.getFullYear();
    const monthShortHand = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    for (let i = 0; i < 15; i++) {
      const month = (startMonth + i + 1) % 12;
      const year = startYear + Math.floor((startMonth + i + 1) / 12);
      arr.push({
        label: `${monthShortHand[month]}-${year}`,
        dateString: `${year}-${month >= 9 ? month + 1 : `0${month + 1}`}-01`,
      });
    }

    return arr;
  };
  const forecastingMonthsEdit: { label: string; dateString: string }[] = getFutureMonthsEdit();

  const handleDirectCurrentTabIndex = (e: { index: number }) => {
    setDirectCurrentTabIndex(e.index);
  };

  const handleIndirectNDCTabChange = (e: { index: number }) => {
    setIndirectCurrentTabIndex(e.index);
  };

  const handleCancel = () => {
    if (
      isNavigationRestricted &&
      unconstrainedForecastEdit.length > 0 &&
      JSON.stringify(unconstrainedForecastEdit) !== JSON.stringify(oldunconstrainedForecastEdit)
    ) {
      if (
        window.confirm(`Are you sure want to navigate?,
Changes done will not be saved!`) // intensional removal of indentation for getting proper formatted message in the alert
      ) {
        navigate(`/fdp/unconstrained_forecast?gfc=${conceptDetails?.gfc}`);
      }
    } else {
      navigate(`/fdp/unconstrained_forecast?gfc=${conceptDetails?.gfc}`);
    }
  };

  // utility function to get sales details
  const getInvoiceSalesDetails = (ndc: string, want: string) => {
    if (invoiceSalesSummary.length === 0) return;

    const ndcSummary = invoiceSalesSummary.filter(item => item.NDC11 === ndc);

    if (ndcSummary.length === 0) {
      return 'NA';
    } else {
      if (want === 'ytd' && ndcSummary[0].YTDAverage !== null) {
        return ndcSummary[0].YTDAverage;
      } else if (want === '3ma' && ndcSummary[0].ThreeMonthAverage !== null) {
        return ndcSummary[0].ThreeMonthAverage;
      } else if (want === 'pm' && ndcSummary[0].LastMonthAverage !== null) {
        return ndcSummary[0].LastMonthAverage;
      } else {
        return 'NA';
      }
    }
  };
  // };

  // functions for handling adjustments values
  function isNumeric(value: string) {
    // Allow values from -100 to 100, with optional decimals
    return /^-?[1-9][0-9]?$|^-?100$/.test(value) || /^(0|[1-9][0-9]{0,7})$/.test(value);
  }

  const getCurrentDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    return `${year}-${month}-01`;
  };

  // Decides Model vs Manual for the value actually being persisted, using a
  // numeric comparison (not the fragile string `==` the old logic used, which
  // could flip to "Manual" on formatting-only differences like "10000" vs
  // "10000.00"). This is computed fresh at save time and never written back
  // into stratCode/savedStratCode/oldStratCode, so the on-screen Strat Code
  // column always keeps showing the value the backend returned.
  const resolveStratCodeForSave = (adjustedValue: string, baseline: string) => {
    return Number(adjustedValue) === Number(baseline) ? 'Model' : 'Manual';
  };

  const saveAllNDCForecast = async () => {
    const payload: {
      NDC11: string;
      PlanningPeriod: string;
      ForecastPeriod: string;
      StratCode: string;
      WholesalerDemandForecast: number;
      Adjustment: number;
      UnconstrainedForecast: number;
      Notes: string;
    }[] = [];
    const planning_period = getCurrentDate();
    for (const ndcData of unconstrainedForecastEdit) {
      ndcData.forecast_data.forEach(forecast => {
        const hasNDCInPayload = payload.some(
          itm => itm.NDC11 === forecast.NDC11 && itm.ForecastPeriod === forecast.ForecastPeriod
        );
        const isValidForecastPeriod = forecastingMonthsEdit.some(
          period => period.dateString === forecast.ForecastPeriod
        );

        let unconstrainedForecast = forecast.AdjForecast;

        if (ndcValueTypeUsing.get(ndcData.NDC11) === 'saved') {
          unconstrainedForecast = forecast.savedAdjValue;
        } else if (ndcValueTypeUsing.get(ndcData.NDC11) === 'old') {
          unconstrainedForecast = forecast.oldAdjForecast;
        }

        if (!hasNDCInPayload && isValidForecastPeriod && forecast.NDC11 !== null) {
          const obj = {
            NDC11: forecast.NDC11,
            PlanningPeriod: planning_period,
            ForecastPeriod: forecast.ForecastPeriod,
            StratCode: resolveStratCodeForSave(unconstrainedForecast, forecast.Baseline),
            WholesalerDemandForecast: Number(forecast.Baseline),
            Adjustment: Number(unconstrainedForecast) - Number(forecast.Baseline),
            UnconstrainedForecast: Number(unconstrainedForecast),
            Notes: 'notes',
            SellingCategory: ndcData.SellingCategory,
          };
          payload.push(obj);
        }
      });
    }
    try {
      setSavingAll(true);
      await unconstrainedForecastService.saveUnconstrainedForecast(payload);
      updateOldeditState();
    } catch (error) {
      console.log('handleSingleSave: ', error);
    } finally {
      setFinishModal(prev => !prev);
      setSavingAll(false);
      setCopyrowInfo(new Set());
      navigate(`/fdp/unconstrained_forecast?gfc=${conceptDetails?.gfc}`, {
        state: { gfc: conceptDetails?.gfc, from: 'finish' },
      });
      requestProgressRefresh();
    }
  };

  const saveCategoryForecast = async () => {
    const payload: {
      NDC11: string;
      PlanningPeriod: string;
      ForecastPeriod: string;
      StratCode: string;
      WholesalerDemandForecast: number;
      Adjustment: number;
      UnconstrainedForecast: number;
      Notes: string;
    }[] = [];
    const ndcs: string[] = [];

    const planning_period = getCurrentDate();
    for (const ndcData of unconstrainedForecastEdit) {
      if (isDirectMode && ndcData.SellingCategory === 'Direct') {
        // filter out the ndcs in Direct category
        ndcData.forecast_data.forEach(forecast => {
          const hasNDCInPayload = payload.some(
            itm => itm.NDC11 === forecast.NDC11 && itm.ForecastPeriod === forecast.ForecastPeriod
          );
          const isValidForecastPeriod = forecastingMonthsEdit.some(
            period => period.dateString === forecast.ForecastPeriod
          );

          let unconstrainedForecast = forecast.AdjForecast;

          if (ndcValueTypeUsing.get(ndcData.NDC11) === 'saved') {
            unconstrainedForecast = forecast.savedAdjValue;
          } else if (ndcValueTypeUsing.get(ndcData.NDC11) === 'old') {
            unconstrainedForecast = forecast.oldAdjForecast;
          }

          if (!hasNDCInPayload && isValidForecastPeriod) {
            ndcs.push(forecast.NDC11);
            const obj = {
              NDC11: forecast.NDC11,
              PlanningPeriod: planning_period,
              ForecastPeriod: forecast.ForecastPeriod,
              StratCode: resolveStratCodeForSave(unconstrainedForecast, forecast.Baseline),
              WholesalerDemandForecast: Number(forecast.Baseline),
              Adjustment: Number(unconstrainedForecast) - Number(forecast.Baseline),
              UnconstrainedForecast: Number(unconstrainedForecast),
              Notes: 'notes',
              SellingCategory: ndcData.SellingCategory,
            };
            payload.push(obj);
          }
        });
      } else if (!isDirectMode && ndcData.SellingCategory === 'Indirect') {
        // filter out the ndcs in Indirect category
        ndcData.forecast_data.forEach(forecast => {
          const hasNDCInPayload = payload.some(
            itm => itm.NDC11 === forecast.NDC11 && itm.ForecastPeriod === forecast.ForecastPeriod
          );
          const isValidForecastPeriod = forecastingMonthsEdit.some(
            period => period.dateString === forecast.ForecastPeriod
          );

          let unconstrainedForecast = forecast.AdjForecast;
          if (ndcValueTypeUsing.get(ndcData.NDC11) === 'saved') {
            unconstrainedForecast = forecast.savedAdjValue;
          } else if (ndcValueTypeUsing.get(ndcData.NDC11) === 'old') {
            unconstrainedForecast = forecast.oldAdjForecast;
          }

          if (!hasNDCInPayload && isValidForecastPeriod) {
            ndcs.push(forecast.NDC11);
            const obj = {
              NDC11: forecast.NDC11,
              PlanningPeriod: planning_period,
              ForecastPeriod: forecast.ForecastPeriod,
              StratCode: resolveStratCodeForSave(unconstrainedForecast, forecast.Baseline),
              WholesalerDemandForecast: Number(forecast.Baseline),
              Adjustment: Number(unconstrainedForecast) - Number(forecast.Baseline),
              UnconstrainedForecast: Number(unconstrainedForecast),
              Notes: 'notes',
              SellingCategory: ndcData.SellingCategory,
            };
            payload.push(obj);
          }
        });
      }
    }

    try {
      setSavingForCategory(true);
      await unconstrainedForecastService.saveUnconstrainedForecast(payload);
      updateOldeditState();
      for (const item of ndcs) {
        isUnconstrainedNoteSaved.delete(item);
      }
    } catch (error) {
      console.log('saveCategoryForecast=> ', error);
    } finally {
      setSavingForCategory(false);
      setCopyrowInfo(new Set());
      setFinishModalForCategory(prev => !prev);
      requestProgressRefresh();
    }
  };

  const saveSingleNDCForecast = async (ndc: string) => {
    const payload: {
      NDC11: string;
      PlanningPeriod: string;
      ForecastPeriod: string;
      StratCode: string;
      WholesalerDemandForecast: number;
      Adjustment: number;
      UnconstrainedForecast: number;
      Notes: string;
    }[] = [];
    const forecast = unconstrainedForecastEdit.filter(item => item.NDC11 === ndc);
    if (forecast.length !== 1) {
      console.log('Forecast filtered length mismatch');
    }
    const planning_period = getCurrentDate();
    for (const item of forecast[0].forecast_data) {
      const hasNDCInPayload = payload.some(
        itm => itm.NDC11 === ndc && itm.ForecastPeriod === item.ForecastPeriod
      );
      const isValidForecastPeriod = forecastingMonthsEdit.some(
        period => period.dateString === item.ForecastPeriod
      );

      let unconstrainedForecast = item.AdjForecast;
      if (ndcValueTypeUsing.get(ndc) === 'saved') {
        unconstrainedForecast = item.savedAdjValue;
      } else if (ndcValueTypeUsing.get(ndc) === 'old') {
        unconstrainedForecast = item.oldAdjForecast;
      }

      if (!hasNDCInPayload && isValidForecastPeriod) {
        const obj = {
          NDC11: item.NDC11,
          PlanningPeriod: planning_period,
          ForecastPeriod: item.ForecastPeriod,
          StratCode: resolveStratCodeForSave(unconstrainedForecast, item.Baseline),
          WholesalerDemandForecast: Number(item.Baseline),
          Adjustment: Number(unconstrainedForecast) - Number(item.Baseline),
          UnconstrainedForecast: Number(unconstrainedForecast),
          Notes: 'notes',
          SellingCategory: forecast[0].SellingCategory,
        };
        payload.push(obj);
      }
    }
    try {
      setSavingSingle(true);
      await unconstrainedForecastService.saveUnconstrainedForecast(payload);
      updateOldeditState();
      isUnconstrainedNoteSaved.delete(ndc);
    } catch (error) {
      console.log('handleSingleSave: ', error);
    } finally {
      setModalVisible(prev => !prev);
      setSavingSingle(false);
      setCopyrowInfo(new Set());
      requestProgressRefresh();
    }
  };

  const handleNoteAdd = (value: string) => {
    try {
      if (value.trim() === '') {
        toast.warning('Cannot save an empty note!!');
      } else if (conceptDetails && conceptDetails.gfc) {
        const ndc = isDirectMode
          ? directNDCheading[directcurrentNDCIndex]?.ndc
          : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;
        saveForecastNote(ndc, value, 'unconstrained');
      }
    } catch {
      toast.error('Note could not be saved, please try again later');
    }
  };

  const handleEditNote = async (id: number, note: string) => {
    try {
      setIsNoteEdited(prev => {
        const temp = new Map(prev);
        if (temp.has(id)) {
          temp.set(id, true);
        } else {
          temp.set(id, true);
        }
        return temp;
      });
      const data = await unconstrainedForecastService.updateNote(id, note);
      if (data.status) {
        UnconstrainedForecastNotesData.map(item => {
          if (item.ID === id) {
            item.CreatedDate = convertIsoToDateString(data.lastUpdatedDate) || '';
          }
        });
        toast.success('Note Updated successfully.');
      }
    } catch (error) {
      console.log('handleEditNote=> ', error);
      toast.error('Something went wrong.');
    } finally {
      setIsNoteEdited(prev => {
        const temp = new Map(prev);
        if (temp.has(id)) {
          temp.set(id, false);
        } else {
          if (temp.get(id)) {
            temp.set(id, false);
          }
        }
        return temp;
      });
    }
  };

  const handleDeleteNote = async (id: number) => {
    try {
      setIsNoteDeleted(prev => {
        const temp = prev;
        if (temp.has(id)) {
          temp.set(id, true);
        } else {
          temp.set(id, true);
        }
        return temp;
      });
      const data = await unconstrainedForecastService.deleteNote(id);
      if (data) {
        toast.success('Note deleted successfully.');
      }
    } catch (error) {
      console.log('handleEditNote=> ', error);
      toast.error('Something went wrong.');
    } finally {
      setIsNoteDeleted(prev => {
        const temp = prev;
        if (temp.has(id)) {
          temp.set(id, false);
        } else {
          temp.set(id, false);
        }
        return temp;
      });
    }
  };

  const updateForecastAdjustment = (value: string, forecastMonthIndex: number) => {
    // check if the value is numeric otherwise return
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;

    value = value.replace(/,/g, '');

    if (value[0] === '0') {
      value = value.replace(/^0+/, '');
    }

    // check if the value is empty string, if then convert it to 0
    if (value === '' || value === '-') {
      value = '0';
    }

    if (!isNumeric(value)) return;

    let ndcIndex = -1;
    for (let i = 0; i < unconstrainedForecastEdit.length; i++) {
      if (unconstrainedForecastEdit[i].NDC11 === ndc) ndcIndex = i;
    }

    // get the particular forecast index details from the unconstraint forecast edit
    const ndcForecastMonthDetails = deepCopy(
      unconstrainedForecastEdit[ndcIndex].forecast_data[forecastMonthIndex]
    );

    // Strat Code reflects whatever the backend/DB had for this row until the
    // user actually edits the adjustment in this session. Once edited, it is
    // recomputed via a numeric Baseline-vs-adjustment comparison (the same
    // resolveStratCodeForSave logic used for the save payload) so the display
    // never lags behind what will actually be persisted - but a numeric
    // comparison, not the old fragile string `==`, so formatting-only
    // differences (e.g. "10000" vs "10000.00") don't falsely flip it to Manual.
    if (ndcUsingPercentage.has(ndc)) {
      const adjVal = Math.round(
        ((Number(value) + 100) * Number(ndcForecastMonthDetails.Baseline)) / 100
      ).toString();
      value = Math.max(Number(value), -100).toString();
      if (ndcValueTypeUsing.get(ndc) === 'saved') {
        ndcForecastMonthDetails.savedAdjPerc = value;
        ndcForecastMonthDetails.savedAdjValue = adjVal;
        ndcForecastMonthDetails.savedStratCode = resolveStratCodeForSave(
          adjVal,
          ndcForecastMonthDetails.Baseline
        );
      } else if (ndcValueTypeUsing.get(ndc) === 'old') {
        ndcForecastMonthDetails.oldPercentAdj = value;
        ndcForecastMonthDetails.oldAdjForecast = adjVal;
        ndcForecastMonthDetails.oldStratCode = resolveStratCodeForSave(
          adjVal,
          ndcForecastMonthDetails.Baseline
        );
      } else {
        ndcForecastMonthDetails.percentadj = value;
        ndcForecastMonthDetails.AdjForecast = adjVal;
        ndcForecastMonthDetails.stratCode = resolveStratCodeForSave(
          adjVal,
          ndcForecastMonthDetails.Baseline
        );
      }
    } else {
      const percentVal = Math.max(
        Math.round((Number(value) * 100) / Number(ndcForecastMonthDetails.Baseline)) - 100,
        -100
      ).toString();
      if (ndcValueTypeUsing.get(ndc) === 'saved') {
        ndcForecastMonthDetails.savedAdjPerc = percentVal;
        ndcForecastMonthDetails.savedAdjValue = value;
        ndcForecastMonthDetails.savedStratCode = resolveStratCodeForSave(
          value,
          ndcForecastMonthDetails.Baseline
        );
      } else if (ndcValueTypeUsing.get(ndc) === 'old') {
        ndcForecastMonthDetails.oldPercentAdj = percentVal;
        ndcForecastMonthDetails.oldAdjForecast = value;
        ndcForecastMonthDetails.oldStratCode = resolveStratCodeForSave(
          value,
          ndcForecastMonthDetails.Baseline
        );
      } else {
        ndcForecastMonthDetails.percentadj = percentVal;
        ndcForecastMonthDetails.AdjForecast = value;
        ndcForecastMonthDetails.stratCode = resolveStratCodeForSave(
          value,
          ndcForecastMonthDetails.Baseline
        );
      }
    }

    updateUnconstrainedForecastEdit(ndcForecastMonthDetails, ndcIndex, forecastMonthIndex);
  };

  // Safety net: walk backward from forecastMonthIndex to the nearest month that
  // actually has a value for the requested field. Guards the extended months
  // (13-15) against showing blank/0 if a specific entry is ever missing/null,
  // independent of whatever fill-forward the backend already applied.
  const getFieldWithCarryForward = <K extends keyof forecastDataInterface>(
    forecastList: forecastDataInterface[] | undefined,
    forecastMonthIndex: number,
    field: K
  ): forecastDataInterface[K] | undefined => {
    if (!forecastList || forecastList.length === 0) return undefined;
    const startIndex = Math.min(forecastMonthIndex, forecastList.length - 1);
    for (let i = startIndex; i >= 0; i--) {
      const val = forecastList[i]?.[field];
      if (val !== null && val !== undefined) return val;
    }
    return undefined;
  };

  const getValueForNdc = (forecastMonthIndex: number) => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;

    let ndcIndex = -1;
    for (let i = 0; i < unconstrainedForecastEdit.length; i++) {
      if (unconstrainedForecastEdit[i].NDC11 === ndc) ndcIndex = i;
    }
    const isPercent = ndcUsingPercentage.has(ndc);
    const fmtNum = (v: string | number | undefined | null) => {
      if (v === null || v === undefined) return '-';
      const n = Number(v);
      return !isNaN(n) ? n.toLocaleString('en-US') : String(v);
    };
    const forecastList = unconstrainedForecastEdit[ndcIndex]?.forecast_data;
    if (ndcValueTypeUsing.has(ndc)) {
      const type = ndcValueTypeUsing.get(ndc);
      if (type === 'saved') {
        return isPercent
          ? getFieldWithCarryForward(forecastList, forecastMonthIndex, 'savedAdjPerc') || '0'
          : fmtNum(getFieldWithCarryForward(forecastList, forecastMonthIndex, 'savedAdjValue'));
      } else if (type === 'old') {
        return isPercent
          ? getFieldWithCarryForward(forecastList, forecastMonthIndex, 'oldPercentAdj') || '0'
          : fmtNum(getFieldWithCarryForward(forecastList, forecastMonthIndex, 'oldAdjForecast'));
      } else {
        return isPercent
          ? getFieldWithCarryForward(forecastList, forecastMonthIndex, 'percentadj') || '0'
          : fmtNum(getFieldWithCarryForward(forecastList, forecastMonthIndex, 'AdjForecast'));
      }
    }
    return '-';
  };

  const getBaseLineValue = (forecastMonthIndex: number) => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;

    let ndcIndex = -1;
    for (let i = 0; i < unconstrainedForecastEdit.length; i++) {
      if (unconstrainedForecastEdit[i].NDC11 === ndc) ndcIndex = i;
    }
    const forecastList = unconstrainedForecastEdit[ndcIndex]?.forecast_data;
    if (ndcValueTypeUsing.has(ndc)) {
      const type = ndcValueTypeUsing.get(ndc);
      if (type === 'saved') {
        return getFieldWithCarryForward(forecastList, forecastMonthIndex, 'savedStratCode');
      } else if (type === 'old') {
        return getFieldWithCarryForward(forecastList, forecastMonthIndex, 'oldStratCode');
      } else {
        return getFieldWithCarryForward(forecastList, forecastMonthIndex, 'stratCode');
      }
    }
    return '-';
  };

  const getNdcData = () => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;
    const filteredNdcsData = unconstrainedForecastEdit.filter(ndcData => ndcData.NDC11 === ndc);

    if (filteredNdcsData.length === 0) return unconstrainedForecastEdit[0];

    const ndcData = filteredNdcsData[0];
    return ndcData;
  };

  const checkIfValueTypeRadioButtonIsDisabled = (valueType: 'saved' | 'old' | 'forecast') => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;

    const filteredNdcsData = unconstrainedForecastEdit.filter(ndcData => ndcData.NDC11 === ndc);

    if (filteredNdcsData.length === 0) return true;

    const ndcData = filteredNdcsData[0];

    if (valueType === 'saved') {
      return !ndcData.saved_values;
    } else if (valueType === 'old') {
      return !ndcData.old_values_present;
    } else {
      return false;
    }
  };

  const checkIfPercentageEnabled = () => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;
    return ndcUsingPercentage.has(ndc) ? true : false;
  };

  const togglePercentageUsing = () => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;

    const tempNdcUsingPercentage = new Set(ndcUsingPercentage);

    if (ndcUsingPercentage.has(ndc)) {
      tempNdcUsingPercentage.delete(ndc);
    } else {
      tempNdcUsingPercentage.add(ndc);
    }

    setNdcUsingPercentage(tempNdcUsingPercentage);
  };

  const isValueTypeRadioButtonChecked = (valueType: valueTypeInterface) => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;
    return ndcValueTypeUsing.has(ndc) && ndcValueTypeUsing.get(ndc) === valueType ? true : false;
  };

  const changeNdcValueType = (valueType: 'saved' | 'old' | 'forecast') => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;
    const tempNdcValueTypeUsing: Map<string, 'saved' | 'old' | 'forecast'> = new Map(
      ndcValueTypeUsing
    );
    tempNdcValueTypeUsing.set(ndc, valueType);
    setNdcValueTypeUsing(tempNdcValueTypeUsing);
  };

  const getAdjForecast = (ndc: string, forecastMonthIndex: number) => {
    const filteredNdcsData = unconstrainedForecastEdit.filter(ndcData => ndcData.NDC11 === ndc);

    if (filteredNdcsData.length === 0) return '-';

    const ndcData = filteredNdcsData[0];
    const field =
      ndcValueTypeUsing.get(ndc) === 'saved'
        ? 'savedAdjValue'
        : ndcValueTypeUsing.get(ndc) === 'old'
          ? 'oldAdjForecast'
          : 'AdjForecast';

    return getFieldWithCarryForward(ndcData.forecast_data, forecastMonthIndex, field) ?? '-';
  };

  const handleCopyValues = (forecastIndex: number) => {
    const lastInputVal = getValueForNdc(forecastIndex);

    if (lastInputVal === '-' || lastInputVal === '') {
      toast.error('Empty value cannot be copied');
      return;
    }

    for (let i = forecastIndex + 1; i < 15; i++) {
      if (lastInputVal !== undefined) {
        updateForecastAdjustment(lastInputVal, i);
      }
    }
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;
    const temp_info = new Set([ndc + forecastIndex]);
    setCopyrowInfo(temp_info);
    toast.success(`Copied ${lastInputVal} to all rows`);
  };

  const handleResetValues = (forecastIndex: number) => {
    if (forecastIndex >= 15) return;

    const ndc_val = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;

    let ndcIndex = -1;
    for (let i = 0; i < unconstrainedForecastEdit.length; i++) {
      if (unconstrainedForecastEdit[i].NDC11 === ndc_val) ndcIndex = i;
    }
    let originalNdcIndex = -1;
    for (let i = 0; i < oldunconstrainedForecastEdit.length; i++) {
      if (oldunconstrainedForecastEdit[i].NDC11 === ndc_val) originalNdcIndex = i;
    }
    if (ndcIndex === -1 || originalNdcIndex === -1) return;

    // Restore the whole original forecast_data record (value AND stratCode
    // together) instead of feeding the old value back through
    // updateForecastAdjustment. That recompute path derives stratCode from a
    // numeric Baseline comparison, which is NOT guaranteed to reproduce the
    // original stratCode - the DB's true value can legitimately differ from
    // "value == current Baseline" if Baseline has since moved. Copying the
    // original record verbatim is the only way to actually revert to it.
    for (let i = forecastIndex; i < 15; i++) {
      const originalMonthDetails = deepCopy(
        oldunconstrainedForecastEdit[originalNdcIndex].forecast_data[i]
      );
      updateUnconstrainedForecastEdit(originalMonthDetails, ndcIndex, i);
    }
    setCopyrowInfo(new Set());
    toast.success('Restored value to default values');
  };

  const isToggleVisible = () => {
    const ndc = isDirectMode
      ? directNDCheading[directcurrentNDCIndex]?.ndc
      : indirectNDCheading[indirectcurrentNDCIndex]?.ndc;

    const filteredNdcsData = unconstrainedForecastEdit.filter(ndcData => ndcData.NDC11 === ndc);

    if (filteredNdcsData.length === 0) return true;

    const ndcData = filteredNdcsData[0];

    if (ndcData.saved_values) return true;
    else if (!ndcData.old_values_present) return true;
    else return false;
  };

  const handleDirectKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (index < 14) {
        directInputFocus.current[index + 1]?.focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index > 0) {
        directInputFocus.current[index - 1]?.focus();
      }
    }
  };

  const handleIndirectKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (index < 14) {
        indirectInputFocus.current[index + 1]?.focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index > 0) {
        indirectInputFocus.current[index - 1]?.focus();
      }
    }
  };

  const handleDirectFocus = (index: number) => {
    setDirectFocused(index);
  };

  const handleIndirectFocus = (index: number) => {
    setIndirectFocused(index);
  };

  const handleDirectBlur = () => {
    setDirectFocused(null);
  };

  const handleIndirectBlur = () => {
    setIndirectFocused(null);
  };

  useEffect(() => {
    if (unconstrainedForecastEdit.length > 0) {
      const tempNdcValueTypeUsing: Map<string, 'saved' | 'old' | 'forecast'> = new Map();
      unconstrainedForecastEdit.forEach(ndcData => {
        if (ndcData.saved_values) {
          tempNdcValueTypeUsing.set(ndcData.NDC11, 'saved');
        } else if (ndcData.old_values_present) {
          tempNdcValueTypeUsing.set(ndcData.NDC11, 'old');
        } else {
          tempNdcValueTypeUsing.set(ndcData.NDC11, 'forecast');
        }
      });
      setNdcValueTypeUsing(tempNdcValueTypeUsing);
    }
  }, [unconstrainedForecastEdit]);

  return {
    userModulesRoles,
    isConceptLoading,
    isWholeSalerDateLoading,
    isInvoiceSalesLoading,
    conceptDetails,
    directcurrentNDCIndex,
    indirectcurrentNDCIndex,
    directNDCheading,
    indirectNDCheading,
    forecastingMonthsEdit,
    unconstrainedForecastEdit,
    isUnconstrainedForecastNotesDataLoading,
    handleDirectCurrentTabIndex,
    handleIndirectNDCTabChange,
    getInvoiceSalesDetails,
    handleGetConceptDetails,
    showSummary,
    setShowSummary,
    modalVisibe,
    setModalVisible,
    finishmodal,
    setFinishModal,
    saveSingleNDCForecast,
    saveAllNDCForecast,
    savingSingle,
    savingAll,
    savingForCategory,
    isDirectMode,
    setIsDirectMode,
    handleCancel,
    contractDetails,
    isUnconstrainedNoteSaving,
    UnconstrainedForecastNotesData,
    handleNoteAdd,
    updateForecastAdjustment,
    getValueForNdc,
    checkIfValueTypeRadioButtonIsDisabled,
    isValueTypeRadioButtonChecked,
    changeNdcValueType,
    getNdcData,
    checkIfPercentageEnabled,
    togglePercentageUsing,
    getAdjForecast,
    finishModalForCategory,
    setFinishModalForCategory,
    saveCategoryForecast,
    handleCopyValues,
    handleResetValues,
    isToggleVisible,
    copyRowInfo,
    handleDeleteNote,
    handleEditNote,
    setNoteData,
    removeNoteData,
    isNoteEdited,
    isNoteDeleted,
    directInputFocus,
    indirectInputFocus,
    directFocused,
    indirectFocused,
    handleDirectKeyPress,
    handleIndirectKeyPress,
    handleDirectFocus,
    handleIndirectFocus,
    handleDirectBlur,
    handleIndirectBlur,
    getBaseLineValue,
  };
};
